import {SubSettingsFormFieldsType} from "../sub-settings/types";

/** The purchase-limit slice of an offering form — all either helper reads. */
type PurchaseLimitFormValues = Pick<
  SubSettingsFormFieldsType,
  "limitUnitsPerTransaction" | "purchaseLimit"
>;

/**
 * The purchase-limit leg of the form↔API translation, shared by digital products, live
 * events and product services.
 *
 * `purchaseLimit` is omitted outright when the coach has not ticked the box — an
 * offering with no limit says nothing about one rather than sending a zero the API
 * would have to mean something by.
 */
export function toPurchaseLimitPayload(values: PurchaseLimitFormValues): {
  purchaseLimit?: number;
} {
  if (!values.limitUnitsPerTransaction) return {};
  if (values.purchaseLimit === null || values.purchaseLimit === undefined) return {};

  return {purchaseLimit: values.purchaseLimit};
}

/**
 * The update direction, which is not the same as the create one.
 *
 * The API persists an update with TypeORM's `update()`, which skips `undefined` — so an
 * omitted key leaves whatever cap the offering already had in place, and a coach could
 * never take one off. An update therefore always states the field: a number, or `null`
 * for no cap. `null` is what clears it — the column is nullable and the checkout path
 * reads null as unlimited.
 */
export function toPurchaseLimitUpdatePayload(values: PurchaseLimitFormValues): {
  purchaseLimit: number | null;
} {
  const {purchaseLimit} = toPurchaseLimitPayload(values);

  return {purchaseLimit: purchaseLimit ?? null};
}

/**
 * The read direction: a saved `purchaseLimit` re-ticks the box on the edit form, and its
 * absence (or a non-positive value from an older record) leaves the box clear.
 */
export function toPurchaseLimitFormValues(
  purchaseLimit: number | null | undefined
): PurchaseLimitFormValues {
  const hasLimit = typeof purchaseLimit === "number" && purchaseLimit > 0;

  return {
    limitUnitsPerTransaction: hasLimit,
    purchaseLimit: hasLimit ? purchaseLimit : undefined,
  };
}
